import { h } from 'preact';
import { useMemo, useEffect } from 'preact/hooks';
import htm from 'htm';
import { marked } from 'marked';
import { transformSuggestedActions } from '../suggestedActions.js';
import { useStreamSmoother } from '../hooks/useStreamSmoother.js';

const html = htm.bind(h);

marked.setOptions({
  breaks: true,
  gfm: true
});

marked.use({
  renderer: {
    link({ href, title, tokens }) {
      const text = this.parser.parseInline(tokens);
      return `<a href="${href}" target="_blank" rel="noopener noreferrer">${text}</a>`;
    }
  }
});

/**
 * AnimatedMessage component that handles the markdown compilation and safe HTML-slicing
 * animation of a chat turn message.
 *
 * @param {Object} props - Component props.
 * @param {Object} props.message - The message data structure.
 * @param {number} [props.typingSpeed] - Optional customized typing speed characters/second.
 * @param {boolean} [props.organicTyping] - Whether organic human-like typewriter simulation is enabled.
 * @param {number} [props.typingCatchUpSeconds] - Max seconds to keep typing after the stream ends (0 = reveal at once).
 * @param {(isTyped: boolean) => void} [props.onTypedChange] - Called when the message becomes fully typed
 *   (stream ended and the whole text is shown) or stops being so.
 */
export function AnimatedMessage({ message, typingSpeed, organicTyping = false, typingCatchUpSeconds = 0, onTypedChange }) {
  // Transform XML action blocks into inline HTML, then compile Markdown.
  // This runs as a single preprocessing pipeline so the typewriter
  // animation reveals action buttons naturally alongside the text.
  const fullHtml = useMemo(() => {
    const transformed = transformSuggestedActions(message.content || '', message.streaming);
    return marked.parse(transformed);
  }, [message.content, message.streaming]);

  const smoothedHtml = useStreamSmoother(fullHtml, message.streaming, typingSpeed, organicTyping, typingCatchUpSeconds);

  const isTyped = !message.streaming && smoothedHtml === fullHtml;
  useEffect(() => {
    if (onTypedChange) onTypedChange(isTyped);
  }, [isTyped]);

  return html`
    <div
      class="mta-turn-text"
      dangerouslySetInnerHTML=${{ __html: smoothedHtml }}
    ></div>
  `;
}
